/**
 * Dashboard Page
 * Main view showing capacity overview and key metrics
 */

import React, { useState } from 'react';
import { useTeamData, useSprints, useCapacity } from '../hooks';
import CapacityChart from '../components/CapacityChart';
import { getStatusColor, formatPercent } from '../utils/format';

const Dashboard: React.FC = () => {
  const [selectedSprint, setSelectedSprint] = useState<string>();
  
  // Fetch data
  const { data: teamData, isLoading: teamLoading } = useTeamData();
  const { data: sprintsData, isLoading: sprintsLoading } = useSprints('active');
  const { data: capacityData, isLoading: capacityLoading } = useCapacity(selectedSprint);

  // Set default sprint
  React.useEffect(() => {
    if (sprintsData?.data && !selectedSprint) {
      const activeSprint = sprintsData.data.find(s => s.status === 'active');
      if (activeSprint) {
        setSelectedSprint(activeSprint.id);
      }
    }
  }, [sprintsData, selectedSprint]);

  const isLoading = teamLoading || sprintsLoading || capacityLoading;
  const members = teamData?.data?.items || [];
  const sprints = sprintsData?.data || [];
  const capacity = capacityData?.data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <h1 className="text-3xl font-bold text-gray-900">Team Resource Planner</h1>
          <p className="text-gray-600 mt-2">Capacity planning and team allocation dashboard</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Controls */}
        <div className="mb-8 flex gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sprint</label>
            <select
              value={selectedSprint || ''}
              onChange={e => setSelectedSprint(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
            >
              <option value="">Select a sprint</option>
              {sprints.map(sprint => (
                <option key={sprint.id} value={sprint.id}>
                  {sprint.name} ({sprint.status})
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 mb-4" />
              <p className="text-gray-600">Loading data...</p>
            </div>
          </div>
        )}

        {!isLoading && capacity && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="card">
                <div className="card-body">
                  <p className="text-sm text-gray-600 mb-2">Total Capacity</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {capacity.totalCapacity?.toFixed(0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">Story Points</p>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <p className="text-sm text-gray-600 mb-2">Allocated</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {capacity.allocatedCapacity?.toFixed(0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">Story Points</p>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <p className="text-sm text-gray-600 mb-2">Utilization</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {formatPercent(
                      capacity.totalCapacity > 0
                        ? (capacity.allocatedCapacity / capacity.totalCapacity) * 100
                        : 0
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">Overall</p>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <p className="text-sm text-gray-600 mb-2">Over-Allocated</p>
                  <p className="text-3xl font-bold text-danger-600">
                    {capacity.overAllocatedCount || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">Team Members</p>
                </div>
              </div>
            </div>

            {/* Capacity Chart */}
            <div className="card mb-8">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Team Capacity</h2>
              </div>
              <div className="card-body">
                {capacity.members && capacity.members.length > 0 ? (
                  <CapacityChart data={capacity.members} height={400} />
                ) : (
                  <p className="text-gray-600 text-center py-8">No capacity data available</p>
                )}
              </div>
            </div>

            {/* Team Members Table */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
              </div>
              <div className="card-body overflow-x-auto">
                {capacity.members && capacity.members.length > 0 ? (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Total Capacity</th>
                        <th>Allocated</th>
                        <th>Available</th>
                        <th>Utilization</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {capacity.members.map(member => {
                        const statusColor = getStatusColor(member.allocationStatus);
                        return (
                          <tr key={member.userId}>
                            <td className="font-medium">{member.user.name}</td>
                            <td>{member.totalCapacityHours.toFixed(1)}h</td>
                            <td>{member.currentAllocationHours.toFixed(1)}h</td>
                            <td>{member.availableCapacityHours.toFixed(1)}h</td>
                            <td>
                              <div className="flex items-center gap-2">
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full ${
                                      member.utilizationPercent > 100
                                        ? 'bg-danger-600'
                                        : member.utilizationPercent > 75
                                          ? 'bg-warning-600'
                                          : 'bg-success-600'
                                    }`}
                                    style={{
                                      width: `${Math.min(member.utilizationPercent, 100)}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-sm font-medium whitespace-nowrap">
                                  {formatPercent(member.utilizationPercent)}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColor.bg} ${statusColor.text} ${statusColor.border}`}
                              >
                                {member.allocationStatus}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-gray-600 text-center py-8">No team members data available</p>
                )}
              </div>
            </div>
          </>
        )}

        {!isLoading && !capacity && (
          <div className="card">
            <div className="card-body text-center py-12">
              <p className="text-gray-600">
                Select a sprint to view capacity data
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
